if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define(
    'cart-drawer-recommendations',
    class CartDrawerRecommendations extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.loadRecommendations();
      }

      disconnectedCallback() {
        this.carouselResizeObserver?.disconnect();
      }

      initializeCarousel() {
        const list = this.querySelector('.cart-drawer-recommendations__list');
        const drawer = this.closest('#CartDrawer');
        const track = drawer?.querySelector('[data-cart-drawer-recommendations-scrollbar]');
        const thumb = track?.querySelector('.cart-drawer__products-divider-thumb');

        if (!list || !track || !thumb) return;

        const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);
        let updateFrame;

        const updateIndicator = () => {
          const maximumScroll = Math.max(list.scrollWidth - list.clientWidth, 0);
          const trackWidth = track.clientWidth;
          const thumbWidth = maximumScroll > 1 ? clamp(trackWidth / 6, 10, trackWidth) : trackWidth;
          const maximumThumbOffset = Math.max(trackWidth - thumbWidth, 0);
          const thumbOffset = maximumScroll ? (list.scrollLeft / maximumScroll) * maximumThumbOffset : 0;

          track.classList.toggle('is-static', maximumScroll <= 1);
          thumb.style.width = `${thumbWidth}px`;
          thumb.style.transform = `translateX(${clamp(thumbOffset, 0, maximumThumbOffset)}px)`;
        };

        const scheduleIndicatorUpdate = () => {
          cancelAnimationFrame(updateFrame);
          updateFrame = requestAnimationFrame(updateIndicator);
        };

        let listPointerId;
        let listStartX = 0;
        let listStartScrollLeft = 0;
        let suppressClick = false;

        list.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button, input')) return;

          listPointerId = event.pointerId;
          listStartX = event.clientX;
          listStartScrollLeft = list.scrollLeft;
          suppressClick = false;
          list.setPointerCapture(event.pointerId);
        });

        list.addEventListener('pointermove', (event) => {
          if (event.pointerId !== listPointerId) return;

          const distance = event.clientX - listStartX;
          if (!suppressClick && Math.abs(distance) < 4) return;

          suppressClick = true;
          list.classList.add('is-dragging');
          list.scrollLeft = listStartScrollLeft - distance;
          scheduleIndicatorUpdate();
          event.preventDefault();
        });

        const finishListDrag = (event) => {
          if (event.pointerId !== listPointerId) return;

          if (list.hasPointerCapture(event.pointerId)) list.releasePointerCapture(event.pointerId);
          listPointerId = undefined;
          list.classList.remove('is-dragging');

          if (suppressClick) {
            setTimeout(() => {
              suppressClick = false;
            });
          }
        };

        list.addEventListener('pointerup', finishListDrag);
        list.addEventListener('pointercancel', finishListDrag);
        list.addEventListener('dragstart', (event) => event.preventDefault());
        list.addEventListener(
          'click',
          (event) => {
            if (!suppressClick) return;
            event.preventDefault();
            event.stopImmediatePropagation();
          },
          true
        );
        list.addEventListener('scroll', scheduleIndicatorUpdate, { passive: true });

        let trackPointerId;
        const scrollFromTrackPointer = (clientX) => {
          const maximumScroll = Math.max(list.scrollWidth - list.clientWidth, 0);
          const trackRect = track.getBoundingClientRect();
          const thumbWidth = thumb.getBoundingClientRect().width;
          const maximumThumbOffset = Math.max(trackRect.width - thumbWidth, 0);
          const thumbOffset = clamp(clientX - trackRect.left - thumbWidth / 2, 0, maximumThumbOffset);

          list.scrollLeft = maximumThumbOffset ? (thumbOffset / maximumThumbOffset) * maximumScroll : 0;
          scheduleIndicatorUpdate();
        };

        track.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0 || track.classList.contains('is-static')) return;

          trackPointerId = event.pointerId;
          track.classList.add('is-dragging');
          list.classList.add('is-dragging');
          track.setPointerCapture(event.pointerId);
          scrollFromTrackPointer(event.clientX);
          event.preventDefault();
        });

        track.addEventListener('pointermove', (event) => {
          if (event.pointerId !== trackPointerId) return;
          scrollFromTrackPointer(event.clientX);
          event.preventDefault();
        });

        const finishTrackDrag = (event) => {
          if (event.pointerId !== trackPointerId) return;

          if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
          trackPointerId = undefined;
          track.classList.remove('is-dragging');
          list.classList.remove('is-dragging');
        };

        track.addEventListener('pointerup', finishTrackDrag);
        track.addEventListener('pointercancel', finishTrackDrag);

        this.carouselResizeObserver?.disconnect();
        this.carouselResizeObserver = new ResizeObserver(scheduleIndicatorUpdate);
        this.carouselResizeObserver.observe(list);
        this.carouselResizeObserver.observe(track);
        scheduleIndicatorUpdate();
      }

      getSourceProductId() {
        const cartProductIds = (this.dataset.cartProductIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);

        let storedProductId = '';
        try {
          storedProductId = sessionStorage.getItem('cartDrawerRecommendationsProductId') || '';
        } catch (error) {
          storedProductId = '';
        }

        if (storedProductId && (!cartProductIds.length || cartProductIds.includes(storedProductId))) {
          return storedProductId;
        }

        return this.dataset.productId || '';
      }

      getRecommendationsUrl(productId) {
        const root = window.Shopify?.routes?.root || '/';
        const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
        const intent = ['complementary', 'related'].includes(this.dataset.intent)
          ? this.dataset.intent
          : 'complementary';
        const limit = Math.min(Math.max(parseInt(this.dataset.limit, 10) || 4, 1), 10);
        const params = new URLSearchParams({
          product_id: productId,
          limit: String(limit),
          intent,
        });

        return `${normalizedRoot}recommendations/products.json?${params.toString()}`;
      }

      escapeHtml(value) {
        const div = document.createElement('div');
        div.textContent = value || '';
        return div.innerHTML;
      }

      formatMoney(cents) {
        const amount = (parseInt(cents, 10) || 0) / 100;
        const currency = this.dataset.currency || window.Shopify?.currency?.active || 'BRL';

        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', {
            style: 'currency',
            currency,
          }).format(amount);
        } catch (error) {
          return `R$ ${amount.toFixed(2).replace('.', ',')}`;
        }
      }

      getProductImage(product) {
        if (!product) return '';
        if (typeof product.featured_image === 'string') return product.featured_image;
        if (product.featured_image?.src) return product.featured_image.src;
        if (Array.isArray(product.images) && product.images.length) return product.images[0];
        return '';
      }

      getProductUrl(product) {
        const root = window.Shopify?.routes?.root || '/';
        const normalizedRoot = root.endsWith('/') ? root : `${root}/`;

        if (product.url) return product.url;
        if (product.handle) return `${normalizedRoot}products/${product.handle}`;
        return '#';
      }

      isSimpleProduct(product, variant) {
        if (!product || !variant) return false;
        if (product.has_only_default_variant === true) return true;
        if (product.variants?.length === 1 && variant.title === 'Default Title') return true;
        return false;
      }

      renderBagIcon() {
        return `
          <svg aria-hidden="true" focusable="false" class="cart-drawer-recommendation-card__button-icon" viewBox="0 0 24 24">
            <path d="M6.75 8.25h10.5l.75 11.25H6L6.75 8.25Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path>
            <path d="M9 8.25V6.75a3 3 0 0 1 6 0v1.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
          </svg>
        `;
      }

      renderCard(product, index) {
        const variant = product.variants?.find((item) => item.available) || product.variants?.[0];
        if (!variant) return '';

        const image = this.getProductImage(product);
        const productUrl = this.getProductUrl(product);
        const productTitle = this.escapeHtml(product.title);
        const recommendationTitle = this.escapeHtml(this.dataset.heading || 'Leve tambem!');
        const price = variant.price || product.price;
        const compareAtPrice = variant.compare_at_price || product.compare_at_price;
        const isAvailable = Boolean(variant.available);
        const canAddToCart = isAvailable && this.isSimpleProduct(product, variant);
        const buttonLabel = this.escapeHtml(this.dataset.addLabel || 'Adicionar');
        const buttonIcon = this.renderBagIcon();
        const quantityMinimum = Math.max(parseInt(variant.quantity_rule?.min, 10) || 1, 1);
        const quantityIncrement = Math.max(parseInt(variant.quantity_rule?.increment, 10) || 1, 1);
        const quantityMaximum = parseInt(variant.quantity_rule?.max, 10);
        const quantityMaximumAttribute = Number.isFinite(quantityMaximum) ? ` max="${quantityMaximum}"` : '';
        const buttonMarkup = canAddToCart
          ? `
            <product-form class="cart-drawer-recommendation-card__form">
              <div class="product-form__error-message-wrapper" role="alert" hidden>
                <span class="product-form__error-message"></span>
              </div>
              <form method="post" action="${window.routes?.cart_add_url || '/cart/add'}" class="form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">
                <input type="hidden" name="id" value="${variant.id}">
                <div class="cart-drawer-recommendation-card__actions">
                  <quantity-input class="quantity cart-drawer-recommendation-card__quantity">
                    <button class="quantity__button" name="minus" type="button" aria-label="${this.escapeHtml(`Diminuir quantidade de ${product.title}`)}">
                      <svg aria-hidden="true" focusable="false" viewBox="0 0 12 12"><path d="M2 6h8" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"></path></svg>
                    </button>
                    <input
                      class="quantity__input"
                      type="number"
                      name="quantity"
                      value="${quantityMinimum}"
                      min="${quantityMinimum}"
                      data-min="${quantityMinimum}"
                      step="${quantityIncrement}"
                      ${quantityMaximumAttribute}
                      aria-label="${this.escapeHtml(`Quantidade para ${product.title}`)}"
                    >
                    <button class="quantity__button" name="plus" type="button" aria-label="${this.escapeHtml(`Aumentar quantidade de ${product.title}`)}">
                      <svg aria-hidden="true" focusable="false" viewBox="0 0 12 12"><path d="M2 6h8M6 2v8" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"></path></svg>
                    </button>
                  </quantity-input>
                  <button type="submit" name="add" class="cart-drawer-recommendation-card__button" aria-label="${this.escapeHtml(`${this.dataset.addLabel || 'Adicionar'} ${product.title}`)}">
                    <span class="visually-hidden">${buttonLabel}</span>
                    ${buttonIcon}
                    <div class="loading__spinner hidden">
                      <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                        <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                      </svg>
                    </div>
                  </button>
                </div>
              </form>
            </product-form>`
          : isAvailable
            ? `<a href="${this.escapeHtml(productUrl)}" class="cart-drawer-recommendation-card__button" aria-label="${this.escapeHtml(`${this.dataset.viewOptionsLabel || 'Ver opcoes'} ${product.title}`)}"><span class="visually-hidden">${this.escapeHtml(this.dataset.viewOptionsLabel || 'Ver opcoes')}</span>${buttonIcon}</a>`
            : `<span class="cart-drawer-recommendation-card__button cart-drawer-recommendation-card__button--disabled">${this.escapeHtml(this.dataset.soldOutLabel || 'Esgotado')}</span>`;

        return `
          <li id="Slide-CartDrawerRecommendations-${index + 1}" class="cart-drawer-recommendations__slide grid__item slider__slide">
            <article class="cart-drawer-recommendation-card">
              <p class="cart-drawer-recommendation-card__eyebrow">${recommendationTitle}</p>
              <div class="cart-drawer-recommendation-card__main">
                <a href="${this.escapeHtml(productUrl)}" class="cart-drawer-recommendation-card__media" tabindex="-1" aria-hidden="true">
                  ${
                    image
                      ? `<img src="${this.escapeHtml(image)}" alt="${productTitle}" loading="lazy" width="64" height="64">`
                      : '<svg class="placeholder-svg" viewBox="0 0 525.5 525.5" xmlns="http://www.w3.org/2000/svg" role="presentation"></svg>'
                  }
                </a>

                <div class="cart-drawer-recommendation-card__body">
                  <div class="cart-drawer-recommendation-card__content">
                    <a href="${this.escapeHtml(productUrl)}" class="cart-drawer-recommendation-card__title">${productTitle}</a>

                    <div class="cart-drawer-recommendation-card__price">
                      ${compareAtPrice && compareAtPrice > price ? `<s>${this.formatMoney(compareAtPrice)}</s>` : ''}
                      <span>${this.formatMoney(price)}</span>
                    </div>
                  </div>

                  ${buttonMarkup}
                </div>
              </div>
            </article>
          </li>
        `;
      }

      renderProducts(products) {
        const cartProductIds = (this.dataset.cartProductIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);
        const recommendedProducts = products.filter((product) => !cartProductIds.includes(String(product.id)));
        const cards = recommendedProducts
          .map((product, index) => this.renderCard(product, index))
          .join('')
          .trim();

        if (!cards) return '';

        const sliderId = 'Slider-CartDrawerRecommendations';
        const configuredLimit = Math.min(Math.max(parseInt(this.dataset.limit, 10) || 4, 1), 10);
        const showControls = configuredLimit > 1 && recommendedProducts.length > 1;

        return `
          <div class="cart-drawer-recommendations__inner">
            <slider-component class="cart-drawer-recommendations__slider">
              <ul id="${sliderId}" class="cart-drawer-recommendations__list grid grid--1-col slider slider--everywhere" role="list" aria-label="${this.escapeHtml(this.dataset.heading || 'Produtos recomendados')}">
                ${cards}
              </ul>
              ${
                showControls
                  ? `
                    <div class="cart-drawer-recommendations__controls slider-buttons">
                      <button type="button" class="slider-button slider-button--prev" name="previous" aria-label="Slide anterior" aria-controls="${sliderId}">
                        <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
                        </svg>
                      </button>
                      <div class="slider-counter caption">
                        <span class="slider-counter--current">1</span>
                        <span aria-hidden="true"> / </span>
                        <span class="visually-hidden">de</span>
                        <span class="slider-counter--total">${recommendedProducts.length}</span>
                      </div>
                      <button type="button" class="slider-button slider-button--next" name="next" aria-label="Proximo slide" aria-controls="${sliderId}">
                        <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
                        </svg>
                      </button>
                    </div>
                  `
                  : ''
              }
            </slider-component>
          </div>
        `;
      }

      loadRecommendations() {
        const productId = this.getSourceProductId();

        if (!productId) {
          this.hidden = true;
          return;
        }

        this.classList.add('is-loading');

        fetch(this.getRecommendationsUrl(productId))
          .then((response) => {
            if (!response.ok) throw new Error(response.status);
            return response.json();
          })
          .then((responseJson) => {
            const content = this.renderProducts(responseJson.products || []);

            if (!content) {
              this.hidden = true;
              return;
            }

            this.innerHTML = content;
            this.hidden = false;
            this.classList.add('is-loaded');
            this.initializeCarousel();
          })
          .catch(() => {
            this.hidden = true;
          })
          .finally(() => {
            this.classList.remove('is-loading');
          });
      }
    }
  );
}
